import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "What we collect",
    body: "Account details (name, email), licence and verification data you submit, documents you upload, shift activity (claims, check-in and checkout times), GPS coordinates at the moment of check-in and checkout (never continuous tracking), payment references (card details are handled entirely by Stripe — we never see or store them), and support conversations.",
  },
  {
    title: "How we use it",
    body: "To verify SIA licences, match guards with shifts, prove shift attendance (GPS at check-in/out), process payouts, enforce platform rules (including the no-show ban), send launch and shift-notification emails, and operate the panic-alert safety system. We never sell your data.",
  },
  {
    title: "Location data",
    body: "Location is captured only when you tap check-in, check out, or the panic button during an active shift. We do not track your location in the background or at any other time.",
  },
  {
    title: "Who we share with",
    body: "Venues see your name, verification status, and reliability score when you claim their shift. Service providers who run the platform (hosting, Stripe for payments, email delivery) process data under their own security terms. We disclose data only when legally required.",
  },
  {
    title: "UK GDPR & your rights",
    body: "We process personal data under the UK GDPR and the Data Protection Act 2018. You have the right to access, correct, export, or erase your personal data, and to object to or restrict how we process it. Account data is kept while your account is active; ban and no-show records are retained to enforce platform rules, and completed-shift records are kept for payment and legal compliance. Deleting your account removes your profile but not those records.",
  },
  {
    title: "Complaints & the ICO",
    body: "If you're unhappy with how we handle your data, contact us first — we answer within 72 hours and will do our best to put things right. You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk, the UK regulator for data protection.",
  },
  {
    title: "Security",
    body: "Passwords are bcrypt-hashed, sessions use short-lived signed tokens in httpOnly cookies, files are stored in encrypted object storage, payment flows run on Stripe's PCI-compliant infrastructure, and all traffic is HTTPS.",
  },
  {
    title: "Contact",
    body: "Questions or data requests: email support@guardgig28.com or reach us through the site. We answer within 72 hours.",
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#07090e] pb-24">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="privacy-back" className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Back</span>
          </Link>
          <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          <span className="font-mono-tag text-[9px] text-slate-600">guardgig28.com</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pt-16 sm:px-6">
        <p className="font-mono-tag text-[11px] text-amber-500">Legal</p>
        <h1 className="font-display mt-4 text-4xl font-black uppercase tracking-tight text-slate-50 sm:text-5xl">Privacy Policy</h1>
        <p className="mt-3 font-mono-tag text-[10px] text-slate-500">Effective September 2026 · GuardGig28 · United Kingdom</p>

        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400">
          Plain-English version: we collect only what a security staffing platform needs to
          prove who showed up, where, and that they got paid. We never sell data, and we never
          track your location outside an active shift.
        </p>

        <div className="mt-12 space-y-6">
          {sections.map((s, i) => (
            <section key={s.title} data-testid={`privacy-section-${i}`} className="rounded-xl border border-white/8 bg-[#0f141c] p-7">
              <h2 className="font-display text-lg font-bold uppercase text-slate-50">
                <span className="mr-3 text-amber-500">{String(i + 1).padStart(2, "0")}</span>{s.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 flex items-center gap-2 text-slate-600">
          <ShieldCheck className="h-4 w-4" />
          <p className="font-mono-tag text-[9px]">GuardGig28 · Serious about work. Fair about everything else.</p>
        </div>
      </main>
    </div>
  );
}
